import { lazy, Suspense, useContext, useEffect, useState } from "react";
import { FaLocationArrow, FaTimes } from "react-icons/fa";
import { IoMdLocate } from "react-icons/io";
import { useDispatch, useSelector } from "react-redux";
import AddressAutocomplete from "../Components/Common/Map/AddressAutocomplete";
import MapSkeleton from "../Components/Common/Map/MapSkeleton";
import { LanguageContext } from "../Context/LanguageContext";
import { fetchLocation } from "../Redux/State-slice/Location";
import { DEFAULT_CENTER } from "../services/mapbox";
import labels from "../translationData/menu.json";
import getTranslation from "./getTranslationUtility";

// Split out of the main bundle — see features/shared/Map.jsx for the reasoning.
const MapboxPicker = lazy(() => import("../Components/Common/Map/MapboxPicker"));

/**
 * "Point your location" modal: search for an address, or drop the pin on the
 * map. Both paths produce a `{ address, lat, lng }` which is handed up via
 * `handleSelect` — so the caller never has to geocode anything itself.
 */
const MapSearch = ({ userAddress, setUserAddress, handleSelect }) => {
  const { coordinate } = useSelector((state) => state.location);
  const dispatch = useDispatch();
  const { currentLanguage } = useContext(LanguageContext);

  const [picked, setPicked] = useState({
    lat: parseFloat(coordinate?.lat) || DEFAULT_CENTER.lat,
    lng: parseFloat(coordinate?.lon) || DEFAULT_CENTER.lng,
  });
  const [isReady, setIsReady] = useState(false);

  // Follow the store when "locate me" resolves a new position.
  useEffect(() => {
    const lat = parseFloat(coordinate?.lat);
    const lng = parseFloat(coordinate?.lon);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      setPicked({ lat, lng });
    }
  }, [coordinate]);

  // Chosen from the search suggestions — coordinates come with the suggestion.
  const handleSuggestion = (place) => {
    setPicked({ lat: place.lat, lng: place.lng });
    setUserAddress(place.address);
    setIsReady(true);
  };

  // Dropped or dragged on the map — already reverse-geocoded by the picker.
  const handleMapPick = (place) => {
    setPicked({ lat: place.lat, lng: place.lng });
    if (place.address) setUserAddress(place.address);
    setIsReady(true);
  };

  const handleClearInput = () => {
    setUserAddress("");
    setIsReady(false);
  };

  const handleSubmit = () => {
    if (!isReady || !userAddress) return;
    handleSelect({ address: userAddress, lat: picked.lat, lng: picked.lng });
    setIsReady(false);
  };

  const handleLocateMe = () => dispatch(fetchLocation());

  return (
    <div>
      <div className="w-full position-relative">
        <AddressAutocomplete
          value={userAddress}
          onChange={(value) => {
            setUserAddress(value);
            setIsReady(false);
          }}
          onSelect={handleSuggestion}
          proximity={picked}
          placeholder="Search Places ..."
          className="w-100 p-3 rounded border border-primary border-2 shadow-sm location-search-input"
        >
          {userAddress ? (
            <button className="clear-button position-absolute" onClick={handleClearInput}>
              <FaTimes />
            </button>
          ) : (
            <div
              style={{ top: "30%" }}
              className="d-flex position-absolute end-0 justify-center align-items-center justify-content-center locate-btn"
              onClick={handleLocateMe}
            >
              <IoMdLocate />
              <span className="mx-1 locate-text">
                {getTranslation("locateMe", currentLanguage, labels)}
              </span>
            </div>
          )}

          {isReady && (
            <button
              className="submit-button position-absolute btn btn-primary"
              onClick={handleSubmit}
            >
              <FaLocationArrow /> Submit
            </button>
          )}
        </AddressAutocomplete>
      </div>

      <div className="mt-1">
        <Suspense fallback={<MapSkeleton height={400} />}>
          <MapboxPicker value={picked} onPick={handleMapPick} height={400} />
        </Suspense>
      </div>
    </div>
  );
};

export default MapSearch;
